import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { VisuallyHidden } from "@pages/AdminDashboard/ui/DataTable/visuallyHidden";
import { ErrorMessage, useFieldId } from "@pages/AdminDashboard/ui/FieldFrame";

const { color, fieldTone, radius, typeScale } = adminTokens;
const [, spaceSm, spaceMd] = adminTokens.spacing;
// The design's shipping method cards: 40px tall, a 14px marker, a 11px tag.
const cardHeight = 40;
const markerSize = 14;
const markerInset = 3;
const tagFontSize = "11px";
const legendGap = 6;
// On a phone the cards stand in one column, each at least 52px (PHONE-R36).
const phoneCardMinHeight = 52;

const Fieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const Legend = styled.legend`
  margin-bottom: ${legendGap}px;
  padding: 0;
  color: ${color.navy};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const ErrorSlot = styled.div`
  margin-top: ${legendGap}px;
`;

const Grid = styled.div<{ $isPhone: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isPhone }) =>
    $isPhone ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))"};
  gap: ${spaceSm}px;
`;

const Option = styled.label`
  position: relative;
  display: block;
`;

// The real radio lies, invisible, over the whole card, so a click anywhere on the
// card chooses it and the arrow keys and Tab stay the browser's own (APG Radio Group).
const Input = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Tag = styled.span`
  flex-shrink: 0;
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${tagFontSize};
`;

const Surface = styled.span<{ $isPhone: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spaceMd - 2}px;
  box-sizing: border-box;
  height: ${({ $isPhone }) => ($isPhone ? "auto" : `${cardHeight}px`)};
  min-height: ${({ $isPhone }) => ($isPhone ? phoneCardMinHeight : 0)}px;
  padding: ${({ $isPhone }) => ($isPhone ? spaceSm : 0)}px ${spaceMd}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  background-color: ${color.surface};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};

  ${Input}:checked + & {
    background-color: ${color.canvas};
    border-color: ${color.periwinkle};
  }

  ${Input}:focus-visible + & {
    ${focusOutline}
  }

  ${Input}:disabled + & {
    color: ${color.disabledText};
  }

  ${Input}:disabled + & ${Tag} {
    color: ${color.disabledText};
  }
`;

const Marker = styled.span`
  flex: none;
  box-sizing: border-box;
  width: ${markerSize}px;
  height: ${markerSize}px;
  border: 1.5px solid ${fieldTone.markerMuted};
  border-radius: 50%;
  background-color: ${color.surface};
  box-shadow: inset 0 0 0 ${markerInset}px ${color.surface};

  ${Input}:checked + ${Surface} > & {
    border-color: ${color.tealDark};
    background-color: ${color.tealDark};
  }
`;

// Outside the phone layout a label too wide is cut with an ellipsis (RADIOCARD-R13); on a phone it
// wraps and is shown in full (PHONE-R42).
const OptionLabel = styled.span<{ $isPhone: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: ${({ $isPhone }) => ($isPhone ? "visible" : "hidden")};
  text-overflow: ${({ $isPhone }) => ($isPhone ? "clip" : "ellipsis")};
  white-space: ${({ $isPhone }) => ($isPhone ? "normal" : "nowrap")};
  overflow-wrap: ${({ $isPhone }) => ($isPhone ? "anywhere" : "normal")};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.labelValue.fontSize};
`;

export interface RadioCardOption {
  value: string;
  label: string;
  tag?: string;
  isDisabled?: boolean;
}

export interface RadioCardGroupProps {
  legend: string;
  isLegendHidden?: boolean;
  name: string;
  options: RadioCardOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

// A controlled group: in a react-hook-form form, wrap it in a `Controller` and pass
// its `field.value` and `field.onChange`.
// A two-column grid of option cards with an optional tag, such as "Current"
// (RADIOCARD-R1…R12). The cards are native radios, so the arrow keys and Tab behave
// as the Radio Group pattern asks.
export const RadioCardGroup = ({
  legend,
  isLegendHidden = false,
  name,
  options,
  value,
  onChange,
  error,
}: RadioCardGroupProps) => {
  const isPhone = useIsPhoneLayout();
  const errorId = `${useFieldId()}-error`;

  return (
    <Fieldset aria-describedby={error ? errorId : undefined}>
      {isLegendHidden ? (
        <VisuallyHidden as="legend">{legend}</VisuallyHidden>
      ) : (
        <Legend>{legend}</Legend>
      )}
      <Grid $isPhone={isPhone}>
        {options.map((option) => (
          <Option key={option.value}>
            <Input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={option.isDisabled}
              onChange={() => onChange(option.value)}
            />
            <Surface $isPhone={isPhone}>
              <Marker />
              <OptionLabel $isPhone={isPhone}>{option.label}</OptionLabel>
              {option.tag && <Tag>{option.tag}</Tag>}
            </Surface>
          </Option>
        ))}
      </Grid>
      {error && (
        <ErrorSlot>
          <ErrorMessage id={errorId}>{error}</ErrorMessage>
        </ErrorSlot>
      )}
    </Fieldset>
  );
};
